import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, EyeOff, Mail, Lock, User, Building2, TrendingUp, BarChart3, Shield, ArrowRight } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { API_BASE_URL as API_BASE } from '../config/apiConfig';

const ImageSection = ({ type }) => (
  <div className="relative">
    <div className={`absolute inset-0 bg-gradient-to-br ${type === 'login' ? 'from-blue-600 to-indigo-700' : 'from-indigo-600 to-purple-700'} rounded-3xl transform ${type === 'login' ? 'rotate-3' : '-rotate-3'}`}></div>
    <img
      src={type === 'login'
        ? "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&q=80"
        : "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&q=80"}
      alt={type === 'login' ? "Modern Property Development" : "Property Analytics Dashboard"}
      className="relative rounded-3xl shadow-2xl w-full h-[500px] lg:h-[600px] object-cover"
    />
    <div className="absolute bottom-6 lg:bottom-8 left-6 lg:left-8 right-6 lg:right-8 bg-white/95 backdrop-blur-md rounded-2xl p-4 lg:p-6">
      {type === 'login' ? (
        <>
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-blue-600 rounded-xl">
              <Building2 className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900 text-sm lg:text-base">PropAnalytics</h3>
              <p className="text-xs lg:text-sm text-gray-600">Property Development Platform</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 text-xs lg:text-sm">
            <div className="flex items-center gap-2 text-gray-700">
              <TrendingUp className="w-3 h-3 lg:w-4 lg:h-4 text-blue-600" />
              <span>Market Analytics</span>
            </div>
            <div className="flex items-center gap-2 text-gray-700">
              <BarChart3 className="w-3 h-3 lg:w-4 lg:h-4 text-blue-600" />
              <span>Data Insights</span>
            </div>
          </div>
        </>
      ) : (
        <>
          <h3 className="text-lg lg:text-xl font-bold text-gray-900 mb-3">Join 10,000+ Developers</h3>
          <div className="space-y-2 text-xs lg:text-sm text-gray-700">
            <div className="flex items-center gap-2">
              <Shield className="w-3 h-3 lg:w-4 lg:h-4 text-blue-600" />
              <span>Enterprise-grade security</span>
            </div>
            <div className="flex items-center gap-2">
              <TrendingUp className="w-3 h-3 lg:w-4 lg:h-4 text-blue-600" />
              <span>Real-time market data</span>
            </div>
            <div className="flex items-center gap-2">
              <BarChart3 className="w-3 h-3 lg:w-4 lg:h-4 text-blue-600" />
              <span>Advanced analytics tools</span>
            </div>
          </div>
        </>
      )}
    </div>
  </div>
);

const FormSection = ({
  type,
  formData,
  handleInputChange,
  showPassword,
  setShowPassword,
  error,
  loading,
  isValid,
  handleSubmit,
  handleGoogleLogin,
  switchMode
}) => (
  <div className="w-full max-w-md mx-auto">
    <div className="mb-6 lg:mb-8">
      <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
        {type === 'login' ? 'Welcome Back' : 'Create Account'}
      </h2>
      <p className="text-gray-600 text-sm lg:text-base">
        {type === 'login'
          ? 'Sign in to access your analytics dashboard'
          : 'Start analyzing properties with powerful insights'}
      </p>
    </div>

    <div className="space-y-4 lg:space-y-5">
      {type === 'register' && (
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Full Name
          </label>
          <div className="relative">
            <User className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              name="fullName"
              required
              value={formData.fullName}
              onChange={handleInputChange}
              className="w-full pl-12 pr-4 py-3 lg:py-3.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 transition-colors text-sm lg:text-base"
              placeholder="John Doe"
            />
          </div>
        </div>
      )}

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Email Address
        </label>
        <div className="relative">
          <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="email"
            name="email"
            required
            value={formData.email}
            onChange={handleInputChange}
            className="w-full pl-12 pr-4 py-3 lg:py-3.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 transition-colors text-sm lg:text-base"
            placeholder="you@example.com"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Password
        </label>
        <div className="relative">
          <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type={showPassword ? 'text' : 'password'}
            name="password"
            required
            value={formData.password}
            onChange={handleInputChange}
            className="w-full pl-12 pr-12 py-3 lg:py-3.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 transition-colors text-sm lg:text-base"
            placeholder="••••••••"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {type === 'login' && (
        <div className="flex items-center justify-between">
          <label className="flex items-center text-sm">
            <input type="checkbox" className="mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
            <span className="text-gray-600">Remember me</span>
          </label>
        </div>
      )}

      {error && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="p-3 lg:p-4 bg-red-50 border border-red-200 rounded-xl"
        >
          <p className="text-red-600 text-xs lg:text-sm text-center">{error}</p>
        </motion.div>
      )}

      <motion.button
        type="button"
        onClick={handleSubmit}
        disabled={loading || !isValid}
        whileHover={isValid ? { scale: 1.01 } : {}}
        whileTap={isValid ? { scale: 0.99 } : {}}
        className={`w-full py-3 lg:py-4 rounded-xl font-semibold shadow-lg transition-all duration-300 flex items-center justify-center gap-2 text-sm lg:text-base
          ${isValid
            ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:shadow-xl cursor-pointer'
            : 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-70'}`}
      >
        {loading ? (
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
            className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full"
          />
        ) : (
          <>
            <span>{type === 'login' ? 'Sign In' : 'Create Account'}</span>
            <ArrowRight className="w-5 h-5" />
          </>
        )}
      </motion.button>

      <div className="relative my-5 lg:my-6">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-300"></div>
        </div>
        <div className="relative flex justify-center text-xs lg:text-sm">
          <span className="px-4 bg-gradient-to-br from-blue-50 via-white to-indigo-50 text-gray-500">Or continue with</span>
        </div>
      </div>

      <motion.button
        type="button"
        onClick={handleGoogleLogin}
        whileHover={{ scale: 1.01 }}
        whileTap={{ scale: 0.99 }}
        className="w-full border-2 border-gray-300 text-gray-700 py-3 lg:py-3.5 rounded-xl font-semibold hover:bg-gray-50 transition-all duration-300 flex items-center justify-center gap-3 text-sm lg:text-base"
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
        </svg>
        <span>{type === 'login' ? 'Sign in' : 'Sign up'} with Google</span>
      </motion.button>

      <div className="text-center pt-3 lg:pt-4">
        <p className="text-gray-600 text-sm lg:text-base">
          {type === 'login' ? "Don't have an account?" : "Already have an account?"}{' '}
          <button
            type="button"
            onClick={switchMode}
            className="text-blue-600 hover:text-blue-700 font-semibold hover:underline"
          >
            {type === 'login' ? 'Sign up' : 'Sign in'}
          </button>
        </p>
      </div>
    </div>
  </div>
);

const Auth = () => {
  const location = useLocation();
  const [mode, setMode] = useState('login');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    fullName: '',
  });

  // Set mode based on URL parameter
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const modeParam = urlParams.get('mode');
    if (modeParam === 'login' || modeParam === 'register') {
      setMode(modeParam);
    }
  }, [location.search]);

  // Check for OAuth callback with tokens in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const accessToken = urlParams.get('access_token');
    const refreshToken = urlParams.get('refresh_token');
    const errorParam = urlParams.get('error');

    if (errorParam) {
      setError(decodeURIComponent(errorParam));
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (accessToken && refreshToken) {
      console.log('✅ OAuth tokens received, storing...');
      // Store tokens
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);

      // Clean URL and redirect
      // FIXED: Redirect to auth/callback instead of hardcoded /dashboard
      // This ensures verifyToken is called and role-based redirection happens
      window.location.href = `/auth/callback?access_token=${accessToken}&refresh_token=${refreshToken}`;
    }
  }, []);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  // Validation helpers
  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const isFormValid = () => {
    if (mode === 'login') {
      return isValidEmail(formData.email) && formData.password.length >= 6;
    } else {
      return (
        formData.fullName.trim().length >= 2 &&
        isValidEmail(formData.email) &&
        formData.password.length >= 8
      );
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid()) return;

    setLoading(true);
    setError('');

    try {
      if (mode === 'register') {
        const response = await fetch(`${API_BASE}/auth/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: formData.email,
            password: formData.password,
            full_name: formData.fullName,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Registration failed');
        }

        const data = await response.json();
        localStorage.setItem('accessToken', data.access_token);
        localStorage.setItem('refreshToken', data.refresh_token);

        // FIXED: Redirection based on role after registration
        const redirectPath = data.user?.access_role === 'admin' ? '/admin' : '/dashboard';
        window.location.href = redirectPath;
      } else {
        const formDataObj = new FormData();
        formDataObj.append('username', formData.email);
        formDataObj.append('password', formData.password);

        const response = await fetch(`${API_BASE}/auth/login`, {
          method: 'POST',
          body: formDataObj,
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Login failed');
        }

        const data = await response.json();
        localStorage.setItem('accessToken', data.access_token);
        localStorage.setItem('refreshToken', data.refresh_token);

        // Redirect based on role
        const redirectPath = data.user?.access_role === 'admin' ? '/admin' : '/dashboard';
        window.location.href = redirectPath;
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    console.log('🔵 Redirecting to Google OAuth...');
    console.log('  Backend URL:', API_BASE);
    // Simply redirect to backend OAuth endpoint
    window.location.href = `${API_BASE}/auth/google/login`;
  };

  const switchMode = () => {
    const newMode = mode === 'login' ? 'register' : 'login';
    setMode(newMode);
    // Update URL without reload
    window.history.pushState({}, '', `/auth?mode=${newMode}`);
    setFormData({ email: '', password: '', fullName: '' });
    setShowPassword(false);
    setError('');
  };

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction) => ({
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    })
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex overflow-x-hidden pt-24 lg:pt-0">
        <div className="w-full max-w-7xl mx-auto flex relative">
          {/* Left Side - Image (Hidden on mobile/tablet) */}
          <div className="hidden lg:flex lg:w-1/2 relative items-center justify-center p-12">
            <AnimatePresence mode="wait" custom={mode === 'login' ? -1 : 1}>
              {mode === 'login' ? (
                <motion.div
                  key="left-image"
                  custom={-1}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.5, ease: 'easeInOut' }}
                  className="w-full h-full flex items-center justify-center"
                >
                  <ImageSection type="login" />
                </motion.div>
              ) : (
                <motion.div
                  key="left-form-desktop"
                  custom={1}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.5, ease: 'easeInOut' }}
                  className="w-full"
                >
                  <FormSection
                    type="register"
                    formData={formData}
                    handleInputChange={handleInputChange}
                    showPassword={showPassword}
                    setShowPassword={setShowPassword}
                    error={error}
                    loading={loading}
                    isValid={isFormValid()}
                    handleSubmit={handleSubmit}
                    handleGoogleLogin={handleGoogleLogin}
                    switchMode={switchMode}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Right Side - Form on desktop, All content on mobile/tablet */}
          <div className="w-full lg:w-1/2 relative flex items-center justify-center p-6 sm:p-8 lg:p-12">
            {/* Desktop view */}
            <div className="hidden lg:block w-full">
              <AnimatePresence mode="wait" custom={mode === 'login' ? 1 : -1}>
                {mode === 'login' ? (
                  <motion.div
                    key="right-form"
                    custom={1}
                    variants={slideVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: 0.5, ease: 'easeInOut' }}
                    className="w-full"
                  >
                    <FormSection
                      type="login"
                      formData={formData}
                      handleInputChange={handleInputChange}
                      showPassword={showPassword}
                      setShowPassword={setShowPassword}
                      error={error}
                      loading={loading}
                      isValid={isFormValid()}
                      handleSubmit={handleSubmit}
                      handleGoogleLogin={handleGoogleLogin}
                      switchMode={switchMode}
                    />
                  </motion.div>
                ) : (
                  <motion.div
                    key="right-image"
                    custom={-1}
                    variants={slideVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: 0.5, ease: 'easeInOut' }}
                    className="w-full h-full flex items-center justify-center"
                  >
                    <ImageSection type="register" />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Mobile/Tablet view - Show only forms */}
            <div className="lg:hidden w-full">
              <AnimatePresence mode="wait">
                {mode === 'login' ? (
                  <motion.div
                    key="mobile-login"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <FormSection
                      type="login"
                      formData={formData}
                      handleInputChange={handleInputChange}
                      showPassword={showPassword}
                      setShowPassword={setShowPassword}
                      error={error}
                      loading={loading}
                      isValid={isFormValid()}
                      handleSubmit={handleSubmit}
                      handleGoogleLogin={handleGoogleLogin}
                      switchMode={switchMode}
                    />
                  </motion.div>
                ) : (
                  <motion.div
                    key="mobile-register"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <FormSection
                      type="register"
                      formData={formData}
                      handleInputChange={handleInputChange}
                      showPassword={showPassword}
                      setShowPassword={setShowPassword}
                      error={error}
                      loading={loading}
                      isValid={isFormValid()}
                      handleSubmit={handleSubmit}
                      handleGoogleLogin={handleGoogleLogin}
                      switchMode={switchMode}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Auth;